import type { ProjectData } from 'gql/graphql';
import {
    ContractStatusEnum,
    ProjectContractTypeEnum,
    ProjectMethodologyEnum,
    ProjectStatusEnum,
    WorkOrderChangeStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useDeleteBimOrder } from 'pages/Project/BimProject/hooks/useDeleteBimOrder';
import { useGetBimWorkOrderDataFromProject } from 'pages/Project/BimProject/WorkOrder/hooks';
import { WorkOrderStatusLabels } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { WorkOrderChapters } from 'pages/Project/UerProject/WorkOrder/workorderTypes';
import { methodologyMap } from 'types/methodology';

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export enum TooltipNameKebabTreeWorkOrder {
    ReplaceVOR = 'ReplaceVOR',
    CreateWorkOrderChange = 'CreateWorkOrderChange',
    DeleteWorkOrder = 'DeleteWorkOrder',
    CantRemoveOrderByOrderStatus = 'CantRemoveOrderByOrderStatus',
}

interface IUseAddItemsKebabTreeWorkOrder {
    projectData: ProjectData | undefined | null;
    statusProject: ProjectStatusEnum | undefined;
    orderRules: Record<string, boolean>;
    filters: any;
    itemId: number;
    isActKC2Navs?: boolean;
    isWorkOrderVorTypeXml?: boolean;
    setIsOpenWorkOrderRequisites: (val: boolean) => void;
    setIsOpenWorkOrderCreateChange: (val: boolean) => void;
    handleDownloadWorkOrder: () => void;
    handleDownloadWorkOrderPrices: () => void;
    handleDeleteButton: () => void;
    handleBimExportWorkOrderPrices: () => void;
    statusContract?: ContractStatusEnum;
    statusWorkOrder?: WorkOrderStatusEnum;
    methodology?: string;
    hasInProgress?: boolean;
    isWorkOrderChangeRequired: boolean;
    openCreatingWorkOrderChangeHandler: () => void;
    openCreateClosingWorkOrderChangeHandler: () => void;
}

const getWorkOrderChangeTooltipMsg = ({
    hasWorkOrderChangesValidStatus,
    isFrameContract,
    isWorkOrderStatusOnFormation,
}: {
    hasWorkOrderChangesValidStatus?: boolean;
    isFrameContract: boolean;
    isWorkOrderStatusOnFormation: boolean;
}): string => {
    if (hasWorkOrderChangesValidStatus) {
        return `В составе ${isFrameContract ? 'НЗ' : 'Расчета'} содержится несогласованный Изм. к ${
            isFrameContract ? 'НЗ' : 'Расчету'
        }`;
    }

    return isWorkOrderStatusOnFormation
        ? `Создание "Изм. к ${isFrameContract ? 'НЗ' : 'Расчету'}" доступно только в статусе ${
              isFrameContract ? 'НЗ' : 'Расчета'
          } "В работе"`
        : `Отсутствуют изменения для создания Изм. к ${isFrameContract ? 'НЗ' : 'Расчету'}`;
};

export const useAddItemsKebabTreeWorkOrder = ({
    projectData,
    statusProject,
    statusContract,
    filters,
    orderRules,
    statusWorkOrder,
    itemId,
    isWorkOrderVorTypeXml,
    methodology,
    hasInProgress,
    setIsOpenWorkOrderRequisites,
    setIsOpenWorkOrderCreateChange,
    handleDownloadWorkOrder,
    handleDownloadWorkOrderPrices,
    handleDeleteButton,
    handleBimExportWorkOrderPrices,
    openCreatingWorkOrderChangeHandler,
    isWorkOrderChangeRequired,
    openCreateClosingWorkOrderChangeHandler,
}: IUseAddItemsKebabTreeWorkOrder) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;
    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;
    const isSummaryChapterSelected = filters.chapter === WorkOrderChapters.SUMMARY;
    const isWorkOrderStatusInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;
    const isWorkOrderStatusOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;
    const isWorkOrderStatusCompleted = statusWorkOrder === WorkOrderStatusEnum.Completed;
    const isStatusWorkOrderDelayed = statusWorkOrder === WorkOrderStatusEnum.Delayed;

    const { hasWorkOrderChangesValidStatus } = useGetBimWorkOrderDataFromProject({
        currentOrderId: itemId,
    });

    const isWorkOrderRequisitesDisabled = useMemo(
        () => isSummaryChapterSelected || !isContractStatusWork || !orderRules.canEdit,
        [isSummaryChapterSelected, isContractStatusWork, orderRules.canEdit],
    );

    const isFrameContract = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );

    const handleOpenWorkOrderRequisites = useCallback(
        () => setIsOpenWorkOrderRequisites(true),
        [setIsOpenWorkOrderRequisites],
    );
    const handleOpenWorkOrderCreateChange = useCallback(() => {
        setIsOpenWorkOrderCreateChange(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_create IZM button_pressed',
            projectId: projectData?.uniqueId,
            contractId: projectData?.contractId,
            orderId: itemId,
            orderChangeStatus: WorkOrderChangeStatusEnum.OnFormation,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        itemId,
        projectData?.contractId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectData?.uniqueId,
        sendGpnAnalyticsEvent,
        setIsOpenWorkOrderCreateChange,
    ]);

    const isItemReplaceFileVorDisabled =
        !isContractStatusWork || !isWorkOrderStatusOnFormation || isDisabledByProjectStatus;

    const isItemWorkOrderChangeDisabled =
        !isContractStatusWork || isDisabledByProjectStatus || !isWorkOrderStatusInProgress;

    const isItemWorkOrderCreateDisabled =
        hasWorkOrderChangesValidStatus ||
        !isContractStatusWork ||
        isDisabledByProjectStatus ||
        !isWorkOrderStatusInProgress;

    const isItemWorkOrderDeleteDisabled =
        !isContractStatusWork || !orderRules.canDelete || isDisabledByProjectStatus || !isWorkOrderStatusOnFormation;

    const groupsWorkOrderKebabMenu = [
        {
            id: 1,
        },
        {
            id: 2,
        },
    ];

    const isHasOrderChangeValidStatus = useMemo(() => {
        const currentWorkOrderNav = projectData?.contractNavigation?.data?.workOrderNavs?.find(
            (nav) => nav.id === itemId,
        );

        return currentWorkOrderNav?.workOrderChanges?.some(
            (item) =>
                item.status === WorkOrderChangeStatusEnum.OnFormation ||
                item.status === WorkOrderChangeStatusEnum.Approving,
        );
    }, [itemId, projectData?.contractNavigation?.data?.workOrderNavs]);

    const tooltipMsgCreateChange = useMemo(() => {
        if (isWorkOrderStatusCompleted || isStatusWorkOrderDelayed) {
            return `Работа с НЗ недоступна в статусе "${statusWorkOrder && WorkOrderStatusLabels[statusWorkOrder]}"`;
        }

        if (isHasOrderChangeValidStatus) {
            return 'В составе Наряд-Заказа может быть одновременно сформировано только одно несогласованное Изм. к НЗ';
        }

        return isWorkOrderStatusOnFormation
            ? 'Создание Изм. к НЗ доступно только в статусе НЗ "В работе"'
            : 'Отсутствуют изменения для создания Изм. к НЗ';
    }, [
        isHasOrderChangeValidStatus,
        isStatusWorkOrderDelayed,
        isWorkOrderStatusCompleted,
        isWorkOrderStatusOnFormation,
        statusWorkOrder,
    ]);

    const tooltipMsgDelete = useMemo(() => {
        if (isWorkOrderStatusCompleted || isStatusWorkOrderDelayed) {
            return `Работа с НЗ недоступна в статусе "${statusWorkOrder && WorkOrderStatusLabels[statusWorkOrder]}"`;
        }

        return 'Удаление НЗ возможно только в статусе "На&nbsp;формировании"';
    }, [isStatusWorkOrderDelayed, isWorkOrderStatusCompleted, statusWorkOrder]);

    const {
        orderDeleteBtnTitle,
        deleteBimOrderHandler,
        DeleteOrderErrorHandler,
        DeleteOrderConfirmDialog,
        orderRemoveTooltipText,
    } = useDeleteBimOrder({
        workOrderId: itemId,
    });

    const actionsWorkOrder = useMemo(() => {
        if (methodology === methodologyMap[ProjectMethodologyEnum.Bim]) {
            return [
                ...(isFrameContract
                    ? [
                          {
                              label: 'Создать Изм. к НЗ',
                              action: openCreatingWorkOrderChangeHandler,
                              id: 1,
                              groupId: 1,
                              disabled: isItemWorkOrderCreateDisabled,
                              /*
                               * RIM-7118 - убираем проверку на наличие актов (!hasActsKC2)
                               */
                              haveTooltip: hasWorkOrderChangesValidStatus || isWorkOrderStatusOnFormation,
                              tooltipName: 'cantCreateWorkOrderChange',
                              tooltipMsg: getWorkOrderChangeTooltipMsg({
                                  hasWorkOrderChangesValidStatus,
                                  isFrameContract,
                                  isWorkOrderStatusOnFormation,
                              }),
                          },
                      ]
                    : []),
                {
                    label: `Выгрузить ${isFrameContract ? 'Наряд-Заказ' : 'Расчет по договору'}`,
                    action: handleDownloadWorkOrder,
                    groupId: 1,
                    disabled: !orderRules.canExportExcel,
                },
                {
                    groupId: 1,
                    label: `Выгрузить накопительную ведомость по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                    disabled: !orderRules.canExportExcel || isWorkOrderStatusOnFormation || !hasInProgress,
                    haveTooltip: isWorkOrderStatusOnFormation,
                    tooltipName: 'cantDownloadByOrderStatus',
                    tooltipMsg: `В статусе "На формировании" недоступна выгрузка накопительной по ${
                        isFrameContract ? 'НЗ' : 'Расчету'
                    }`,
                    action: handleBimExportWorkOrderPrices,
                },
                {
                    label: orderDeleteBtnTitle,
                    action: deleteBimOrderHandler,
                    itemId,
                    groupId: 2,
                    disabled: isItemWorkOrderDeleteDisabled,
                    haveTooltip: !isWorkOrderStatusOnFormation,
                    tooltipName: TooltipNameKebabTreeWorkOrder.CantRemoveOrderByOrderStatus,
                    tooltipMsg: orderRemoveTooltipText,
                },
            ];
        }

        return [
            {
                label: 'Выгрузить Наряд-Заказ',
                action: handleDownloadWorkOrder,
                groupId: 1,
                disabled: !orderRules.canExportExcel,
            },
            {
                label: 'Заменить файл ВОР',
                action: () =>
                    navigate(`/projects/${methodology}/${projectData?.uniqueId}/order/${itemId}/replace`, {
                        state: location.pathname,
                    }),
                groupId: 1,
                disabled: !isWorkOrderVorTypeXml || isItemReplaceFileVorDisabled,
                haveTooltip: !isWorkOrderVorTypeXml || isWorkOrderStatusInProgress,
                tooltipName: TooltipNameKebabTreeWorkOrder.ReplaceVOR,
                tooltipMsg: !isWorkOrderVorTypeXml
                    ? 'Доступно только для ВОР формата xml'
                    : 'В статусе НЗ "В работе" недоступна замена файла ВОР',
            },
            {
                label: 'Редактировать данные',
                action: handleOpenWorkOrderRequisites,
                groupId: 1,
                disabled: isWorkOrderRequisitesDisabled || isDisabledByProjectStatus,
            },
            {
                label: 'Создать Изм. к НЗ',
                action: handleOpenWorkOrderCreateChange,
                groupId: 1,
                disabled:
                    isItemWorkOrderChangeDisabled || !workOrderChangeUerRules.canCreate || isHasOrderChangeValidStatus,
                haveTooltip: isWorkOrderStatusOnFormation || isHasOrderChangeValidStatus || isWorkOrderStatusCompleted,
                tooltipName: TooltipNameKebabTreeWorkOrder.CreateWorkOrderChange,
                tooltipMsg: tooltipMsgCreateChange,
            },
            {
                label: 'Создать закрывающий Изм. к НЗ',
                groupId: 1,
                action: openCreateClosingWorkOrderChangeHandler,
                disabled: !isWorkOrderStatusInProgress || !workOrderChangeUerRules?.canCreateClosing,
            },
            {
                label: 'Выгрузить накопительную ведомость по НЗ',
                groupId: 1,
                action: handleDownloadWorkOrderPrices,
                disabled: !(isWorkOrderStatusInProgress || isWorkOrderStatusCompleted),
                haveTooltip: false,
            },
            {
                label: 'Удалить Наряд-Заказ',
                action: handleDeleteButton,
                itemId,
                groupId: 1,
                disabled: isItemWorkOrderDeleteDisabled,
                haveTooltip: isWorkOrderStatusInProgress || isWorkOrderStatusCompleted || isStatusWorkOrderDelayed,
                tooltipName: TooltipNameKebabTreeWorkOrder.DeleteWorkOrder,
                tooltipMsg: tooltipMsgDelete,
            },
        ];
    }, [
        methodology,
        handleDownloadWorkOrder,
        orderRules.canExportExcel,
        isWorkOrderVorTypeXml,
        isItemReplaceFileVorDisabled,
        isWorkOrderStatusInProgress,
        handleOpenWorkOrderRequisites,
        isWorkOrderRequisitesDisabled,
        isDisabledByProjectStatus,
        handleOpenWorkOrderCreateChange,
        isItemWorkOrderChangeDisabled,
        workOrderChangeUerRules.canCreate,
        workOrderChangeUerRules?.canCreateClosing,
        isHasOrderChangeValidStatus,
        isWorkOrderStatusOnFormation,
        isWorkOrderStatusCompleted,
        tooltipMsgCreateChange,
        openCreateClosingWorkOrderChangeHandler,
        handleDownloadWorkOrderPrices,
        handleDeleteButton,
        itemId,
        isItemWorkOrderDeleteDisabled,
        isStatusWorkOrderDelayed,
        tooltipMsgDelete,
        isFrameContract,
        openCreatingWorkOrderChangeHandler,
        isItemWorkOrderCreateDisabled,
        hasWorkOrderChangesValidStatus,
        hasInProgress,
        handleBimExportWorkOrderPrices,
        orderDeleteBtnTitle,
        deleteBimOrderHandler,
        orderRemoveTooltipText,
        navigate,
        projectData?.uniqueId,
        location.pathname,
    ]);

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    return {
        groupsWorkOrderKebabMenu,
        actionsWorkOrder,
        getMenuItemAttributes,
        tooltipPosition,
        DeleteOrderErrorHandler,
        DeleteOrderConfirmDialog,
        onMenuMouseMove,
        onMenuMouseLeave,
        activeTooltipMsg,
    };
};
